import { qz } from "../client";
import { createNamespaceStore } from "../internal/createNamespaceStore";
import {
  type UsbEvent,
  type UsbKeyInput,
  type UsbOpenStreamInput,
  type UsbState,
  type UsbStatus,
} from "./types";

const MAX_USB_EVENTS = 100;

const usbKey = (input: UsbKeyInput) => `${input.vendorId}:${input.productId}`;

const toUsbStatus = (event: UsbEvent, updatedAt: number): UsbStatus => ({
  lastEventKind: event.kind,
  lastException: event.kind === "ERROR" ? event.exception : null,
  lastOutput: event.kind === "RECEIVE" ? event.output : null,
  productId: event.productId,
  updatedAt,
  vendorId: event.vendorId,
});

const parseUsbEvent = (raw: unknown): UsbEvent | null => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const vendorId = typeof data.vendorId === "string" ? data.vendorId : null;
  const productId = typeof data.productId === "string" ? data.productId : null;

  if (!vendorId || !productId) return null;

  if (typeof data.exception === "string") {
    return {
      exception: data.exception,
      kind: "ERROR",
      productId,
      raw,
      vendorId,
    };
  }

  if ("output" in data) {
    return {
      kind: "RECEIVE",
      output: data.output,
      productId,
      raw,
      vendorId,
    };
  }

  return { kind: "STATUS", productId, raw, vendorId };
};

export const usbStore = createNamespaceStore<UsbState, UsbEvent>({
  applyEvent: (state, event, at) => ({
    ...state,
    events: [...state.events, event].slice(-MAX_USB_EVENTS),
    lastEventAt: at,
    statusByDevice: {
      ...state.statusByDevice,
      [usbKey(event)]: toUsbStatus(event, at),
    },
  }),
  createInitialState: () => ({
    activeStreams: {},
    claimedDevices: {},
    events: [],
    lastEventAt: null,
    lastListenerError: null,
    statusByDevice: {},
  }),
  label: "usb",
  parseEvent: parseUsbEvent,
  registerCallback: (handler) => qz.usb.setUsbCallbacks(handler),
});

export const markUsbClaimed = (input: UsbKeyInput, claimed: boolean) => {
  usbStore.setState((s) => ({
    ...s,
    claimedDevices: {
      ...s.claimedDevices,
      [usbKey(input)]: claimed,
    },
  }));
};

export const markUsbStreamOpened = (input: UsbOpenStreamInput) => {
  usbStore.setState((s) => ({
    ...s,
    activeStreams: {
      ...s.activeStreams,
      [usbKey(input)]: input,
    },
  }));
};

export const markUsbStreamClosed = (input: UsbKeyInput) => {
  const key = usbKey(input);
  usbStore.setState((s) => {
    const activeStreams = { ...s.activeStreams };
    delete activeStreams[key];
    return { ...s, activeStreams };
  });
};
